import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from "react";
import { Download, Key } from "lucide-react";
import { Button } from "@/components/ui";
import { useDownloadLicense, downloadLicenseFile } from "@/hooks/useLicenses";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
import { LicenseModal } from "./LicenseModal";
export function LicenseBar({ contractId, license, customerId }) {
    const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
    const { canDownloadLicense, canGenerateLicense, canRegenerateLicense } = usePermissions();
    const downloadMutation = useDownloadLicense();
    const toast = useToast();
    async function handleDownload() {
        if (!license) return;
        try {
            const blob = await downloadMutation.mutateAsync({
                licenseId: license.LicenseId,
                customerId
            });
            downloadLicenseFile(blob, license.LicenseId);
        } catch (error) {
            const message = error instanceof Error ? error.message : "Failed to download license";
            toast.error(message);
        }
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-4 text-sm",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                className: "flex items-center gap-1.5",
                                children: [
                                    /*#__PURE__*/ _jsx(Key, {
                                        className: "h-4 w-4 text-indigo-500"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-gray-700",
                                        children: license ? "License active" : "No active license"
                                    })
                                ]
                            }),
                            license && /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs text-gray-400",
                                children: [
                                    "Expires",
                                    " ",
                                    new Date(license.ExpiryDate).toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric"
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2 sm:justify-end",
                        children: [
                            license && canDownloadLicense && /*#__PURE__*/ _jsxs(Button, {
                                type: "button",
                                variant: "simple",
                                color: "navy-90",
                                onClick: handleDownload,
                                disabled: downloadMutation.isPending,
                                className: "!inline-flex !items-center !justify-center !px-3 !py-1.5 !text-xs",
                                children: [
                                    /*#__PURE__*/ _jsx(Download, {
                                        className: "mr-1.5 h-3.5 w-3.5"
                                    }),
                                    downloadMutation.isPending ? "Downloading..." : "Download .pem"
                                ]
                            }),
                            license && canRegenerateLicense && /*#__PURE__*/ _jsx(Button, {
                                type: "button",
                                variant: "simple",
                                color: "navy-light",
                                onClick: ()=>setIsLicenseModalOpen(true),
                                className: "!inline-flex !items-center !justify-center !px-3 !py-1.5 !text-xs",
                                children: "Replace license"
                            }),
                            !license && canGenerateLicense && /*#__PURE__*/ _jsx(Button, {
                                type: "button",
                                variant: "simple",
                                color: "navy-90",
                                onClick: ()=>setIsLicenseModalOpen(true),
                                className: "!inline-flex !items-center !justify-center !px-3 !py-1.5 !text-xs",
                                children: "Generate license"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(LicenseModal, {
                contractId: contractId,
                isOpen: isLicenseModalOpen,
                mode: license ? "regenerate" : "generate",
                onClose: ()=>setIsLicenseModalOpen(false)
            })
        ]
    });
}
